import { NavigateFunction } from "react-router-dom";
import {
  CHAT_PATH,
  ITEMS_PATH,
  NOTIFICATIONS_PATH,
  PENDING_DEEP_LINK_KEY,
} from "../constants";
import { UserNotification } from "../types";

const PROTECTED_PREFIXES = [CHAT_PATH, NOTIFICATIONS_PATH] as const;

/** Authenticated item detail only: `/items/{itemId}` — not `/items` or `/items/share/...`. */
function isItemDetailDeepLinkPath(path: string): boolean {
  const pathOnly = path.split("?")[0].split("#")[0];
  const match = pathOnly.match(new RegExp(`^${ITEMS_PATH}/([^/]+)/?$`));
  if (!match) {
    return false;
  }
  return match[1] !== "share";
}

export function isProtectedDeepLinkPath(path: string): boolean {
  const normalized = normalizeDeepLinkPath(path);
  if (!normalized) {
    return false;
  }
  if (isItemDetailDeepLinkPath(normalized)) {
    return true;
  }
  return PROTECTED_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}

export function normalizeDeepLinkPath(input: string | null | undefined): string | null {
  if (input == null || typeof input !== "string") {
    return null;
  }
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      if (typeof window !== "undefined" && url.origin !== window.location.origin) {
        return null;
      }
      const path = `${url.pathname}${url.search}${url.hash}`;
      return path.startsWith("/") ? path : `/${path}`;
    }
  } catch {
    return null;
  }

  const pathOnly = trimmed.split("#")[0].split("?")[0];
  if (!pathOnly.startsWith("/")) {
    return `/${pathOnly}`;
  }
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function savePendingDeepLink(path: string): void {
  if (typeof window === "undefined") {
    return;
  }
  const normalized = normalizeDeepLinkPath(path);
  if (!normalized || !isProtectedDeepLinkPath(normalized)) {
    return;
  }
  sessionStorage.setItem(PENDING_DEEP_LINK_KEY, normalized);
}

export function consumePendingDeepLink(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const stored = sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
  sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
  const normalized = normalizeDeepLinkPath(stored);
  if (!normalized || !isProtectedDeepLinkPath(normalized)) {
    return null;
  }
  return normalized;
}

export function getRedirectPathFromLoginSearch(
  search: string
): string | null {
  const params = new URLSearchParams(search);
  const redirect = params.get("redirect");
  return normalizeDeepLinkPath(redirect);
}

export function getNotificationId(
  notification: UserNotification
): string | null {
  return (
    notification.notificationId ||
    notification.id ||
    notification._id ||
    null
  );
}

export function resolveNotificationToPath(
  notification: UserNotification
): string | null {
  if (notification.chatId) {
    return `${CHAT_PATH}/${encodeURIComponent(notification.chatId)}`;
  }
  const notificationId = getNotificationId(notification);
  if (notificationId) {
    return `${NOTIFICATIONS_PATH}/${encodeURIComponent(notificationId)}`;
  }
  return null;
}

export function resolvePushDataToPath(
  data: Record<string, string> | null | undefined
): string | null {
  if (!data) {
    return null;
  }

  const fromUrl = normalizeDeepLinkPath(data.url || data.click_action);
  if (fromUrl && isProtectedDeepLinkPath(fromUrl)) {
    return fromUrl;
  }

  const chatId = data.chatId || data.roomId;
  if (chatId) {
    return `${CHAT_PATH}/${encodeURIComponent(chatId)}`;
  }

  const notificationId = data.notificationId || data.id;
  if (notificationId) {
    return `${NOTIFICATIONS_PATH}/${encodeURIComponent(notificationId)}`;
  }

  return null;
}

export function toAbsoluteDeepLinkUrl(path: string): string {
  if (typeof window === "undefined") {
    return path;
  }
  const normalized = normalizeDeepLinkPath(path) || path;
  return `${window.location.origin}${normalized}`;
}

export function navigateToNotificationPath(
  navigate: NavigateFunction,
  notification: UserNotification
): boolean {
  const path = resolveNotificationToPath(notification);
  if (!path) {
    return false;
  }

  const notificationId = getNotificationId(notification);
  const isNotificationDetailPath =
    notificationId != null &&
    path === `${NOTIFICATIONS_PATH}/${encodeURIComponent(notificationId)}`;

  navigate(
    path,
    isNotificationDetailPath ? { state: { notification } } : undefined
  );
  return true;
}
